import { useEffect, useState } from 'react';
import {
  companiesApi,
  type CompanyDepartment,
  type DepartmentAccessResponse,
} from '@/api/companies.api';
import { employeesApi } from '@/api/employees.api';
import { Button } from '@/components/ui/Button';
import { Callout } from '@/components/ui/Callout';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import type { CompanyResponse } from '@/types/company';
import type { EmployeeResponse } from '@/types/employee';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

interface DepartmentAccessModalProps {
  company: CompanyResponse | null;
  open: boolean;
  onClose: () => void;
}

export function DepartmentAccessModal({ company, open, onClose }: DepartmentAccessModalProps) {
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [departments, setDepartments] = useState<CompanyDepartment[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>([]);
  const [access, setAccess] = useState<DepartmentAccessResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !company) return;
    setLoading(true);
    Promise.all([
      employeesApi.list({ page: 0, size: 200, activeOnly: true }),
      companiesApi.listDepartments(company.id),
    ])
      .then(([empPage, depts]) => {
        setEmployees(empPage.content.filter((e) => e.userId));
        setDepartments(depts);
      })
      .catch((err) => showApiError(err, 'Failed to load access data'))
      .finally(() => setLoading(false));
  }, [open, company]);

  useEffect(() => {
    if (!company || selectedUserId === '') {
      setAccess(null);
      setSelectedDeptIds([]);
      return;
    }
    companiesApi
      .getDepartmentAccess(company.id, selectedUserId)
      .then((res) => {
        setAccess(res);
        setSelectedDeptIds(res.unrestricted ? [] : res.departmentIds);
      })
      .catch((err) => showApiError(err, 'Failed to load department access'));
  }, [company, selectedUserId]);

  const toggleDepartment = (deptId: number) => {
    setSelectedDeptIds((prev) =>
      prev.includes(deptId) ? prev.filter((id) => id !== deptId) : [...prev, deptId],
    );
  };

  const save = async () => {
    if (!company || selectedUserId === '') return;
    setSaving(true);
    try {
      const res = await companiesApi.saveDepartmentAccess(company.id, {
        userId: selectedUserId,
        departmentIds: selectedDeptIds,
      });
      setAccess(res);
      notify.success(
        res.unrestricted ? 'User can access all departments' : 'Department access saved',
      );
      onClose();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={company ? `Department access · ${company.name}` : 'Department access'}
    >
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="grid gap-4">
          <Callout variant="info">
            Leave all departments unchecked to grant full company access. Select one or more to restrict
            the user to those departments only (HR lists, headcount, dashboards).
          </Callout>

          <Select
            label="User"
            placeholder="Select employee with login…"
            value={selectedUserId === '' ? '' : String(selectedUserId)}
            options={employees.map((emp) => ({
              value: String(emp.userId),
              label: `${emp.firstName} ${emp.lastName ?? ''} (${emp.employeeCode})`.trim(),
            }))}
            onChange={(v) => setSelectedUserId(v ? Number(v) : '')}
          />

          {access && (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Current: {access.unrestricted ? 'All departments' : `${access.departmentIds.length} restricted`}
            </p>
          )}

          <div className="max-h-48 space-y-2 overflow-y-auto rounded border border-slate-200 p-3 dark:border-slate-700">
            {departments.length === 0 ? (
              <p className="text-sm text-slate-500">No departments for this company yet.</p>
            ) : (
              departments.map((dept) => (
                <label key={dept.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedDeptIds.includes(dept.id)}
                    onChange={() => toggleDepartment(dept.id)}
                  />
                  <span>
                    {dept.name} ({dept.code})
                  </span>
                </label>
              ))
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || selectedUserId === ''}>
              Save access
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
